// Defaults for a NEW job allocation, shared by the full AllocationForm on the
// Allocations tab and the one-screen QuickCrewAdd modal so the two paths can
// never seed a row differently. Pure — no fetches, no React.

// Contextual verifications: the boxes that only apply under some PD / travel
// choice flip to N/A when they don't apply (and back to Pending when they
// do). Existing values survive a re-run unless they were N/A.
export const computeContextualVerifications = (pdType, travelType, existing = {}) => {
  const keepOrPending = (cur) => (cur && cur !== 'N/A') ? cur : 'Pending';
  const out = {};
  // PD setup is irrelevant when there's no per diem
  out.verify_pd_setup = (pdType === 'None' || !pdType)
    ? 'N/A' : keepOrPending(existing.verify_pd_setup);
  // Hotel only matters when the PD type includes lodging
  const hasHotel = pdType === 'Full Per Diem' || pdType === 'Hotel Only';
  out.verify_hotel_booked = hasHotel ? keepOrPending(existing.verify_hotel_booked) : 'N/A';
  // Travel confirmation is irrelevant when local
  out.verify_travel_conf = (travelType === 'Local (No Travel)' || !travelType)
    ? 'N/A' : keepOrPending(existing.verify_travel_conf);
  // Flights only when flying
  const isFlying = travelType === 'Flying';
  out.verify_flt_out    = isFlying ? keepOrPending(existing.verify_flt_out)    : 'N/A';
  out.verify_flt_return = isFlying ? keepOrPending(existing.verify_flt_return) : 'N/A';
  return out;
};

// The job window an allocation inherits: start = planned date, end = planned
// date + duration_days (the same `jobEndIso` rule the server uses). UTC date
// math so a DST edge cannot add or drop a day.
export const jobWindow = (job) => {
  const start = job && job.date_planned ? String(job.date_planned).slice(0, 10) : '';
  let end = '';
  const dur = parseInt(job && job.duration_days, 10);
  if (start && dur > 0) {
    const d = new Date(start + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + dur);
    end = d.toISOString().slice(0, 10);
  }
  return { start, end };
};

// Pay rates are snapshotted from the employee's roster defaults exactly as
// AllocationForm.setEmployee does (the server nulls them for non-admin-like
// callers — BIZ-02 — so sending them is harmless for ops). PD None + Local
// travel are the form's defaults; the contextual verifications follow.
export function newAllocationDefaults({ project_id, employee, craft, shift, start_date, end_date }) {
  const payST = employee?.pay_st_default ?? '';
  let payOT = employee?.pay_ot_default ?? '';
  if (payST && !payOT) {
    const n = parseFloat(payST);
    if (isFinite(n)) payOT = (n * 1.5).toFixed(2);
  }
  const pdType = 'None';
  const travelType = 'Local (No Travel)';
  return {
    project_id, employee_id: employee.id, craft, shift: shift || 'Day',
    pay_st: payST, pay_ot: payOT, bill_st: '', bill_ot: '',
    start_date: start_date || '', end_date: end_date || '',
    pd_type: pdType, pd_amount: '', hotel_amount: '', meal_amount: '', pd_start_date: '', pd_load: '',
    travel_type: travelType, travel_rate: '', bill_miles: '', pay_miles: '', rt_miles: '',
    hotel_in_date: '', hotel_out_date: '',
    verify_safety_council: 'Pending', verify_disa: 'Pending', verify_drug_test: 'Pending',
    verify_emp_notified: 'Pending', verify_emrg_contact: 'Pending', verify_paylocity: 'Pending',
    ...computeContextualVerifications(pdType, travelType),
    notes: '',
  };
}
